import { useMemo } from 'react';
import type { BureauStockLevel } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useBureauStock, usePurchaseList, useSetStockLevel } from '../../api/bureau.js';
import { FollowRow } from './FollowRow.js';

const LEVELS: Array<[BureauStockLevel, string]> = [['OK', 'OK'], ['LOW', 'Bas'], ['OUT', 'Épuisé']];

export function StockView({ canManage }: { canManage: boolean }) {
  const stock = useBureauStock();
  const setLevel = useSetStockLevel();
  const list = usePurchaseList();
  const items = stock.data?.items ?? [];
  const groups = useMemo(() => {
    const m = new Map<string, typeof items>();
    for (const i of items) m.set(i.category, [...(m.get(i.category) ?? []), i]);
    return [...m.entries()];
  }, [items]);
  const toBuy = items.filter((i) => i.level !== 'OK');
  const openList = stock.data?.openList ?? null;

  return (
    <div className="bx-stock">
      {openList ? (
        <div className="card bx-list">
          <div className="card-header"><div className="card-title">Liste d’achats en cours</div></div>
          <FollowRow f={openList} canManage={canManage} defaultOpen />
        </div>
      ) : (
        <div className={`card bx-buy ${toBuy.some((i) => i.level === 'OUT') ? 'is-out' : toBuy.length ? 'is-low' : 'is-ok'}`}>
          <div className="bx-buy-head">
            <span className="bx-buy-title">À acheter <span className="bx-mono">{toBuy.length}</span></span>
            {toBuy.length > 0 && (
              <button
                type="button" className="btn btn-sm btn-primary"
                onClick={() => list.mutate(undefined, {
                  onSuccess: () => toast.success('Liste envoyée pour validation'),
                  onError: () => toast.danger('La liste n’a pas pu être envoyée.'),
                })}
              >
                Envoyer la liste pour validation →
              </button>
            )}
          </div>
          <div className="bx-buy-list">
            {toBuy.length
              ? toBuy.map((i) => `${i.label}${i.level === 'OUT' ? ' (épuisé)' : ''}`).join(' · ')
              : 'Rien à acheter, tout est en stock.'}
          </div>
          <div className="bx-hint">Circuit : validation de Bouchra → transmise à Aziz → réception vérifiée → rangée.</div>
        </div>
      )}

      <div className="bx-stock-grid">
        {groups.map(([cat, list]) => (
          <div className="card bx-stock-group" key={cat}>
            <div className="bx-eyebrow bx-stock-cat">{cat}</div>
            {list.map((i) => (
              <div className="bx-stock-row" key={i.id}>
                <span className="bx-stock-label">{i.label}</span>
                <div className="bx-levels" role="radiogroup" aria-label={`Niveau — ${i.label}`}>
                  {LEVELS.map(([lv, label]) => (
                    <button
                      type="button" key={lv} role="radio" aria-checked={i.level === lv}
                      className={i.level === lv ? `bx-level is-on lv-${lv}` : 'bx-level'}
                      onClick={() => setLevel.mutate({ id: i.id, level: lv })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
